import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { ActivateBenefit, ActivateBenefitContainer } from './LoginScreen.styled';
import { Button } from 'components/v2/Buttons/Button/Button';
import { AuthFlowLayout } from 'components/v2/Layouts/AuthFlowLayout/AuthFlowLayout';
import { Text } from 'components/v2/Typography/Text';
import { loginErrorHandled } from 'kb-redux/user.redux';
import { GoogleAuthError } from 'kb-shared';
import { analytics } from 'utilities/analytics';
import {
  DEACTIVATED_ACCESS_QUERY_PARAM,
  DEACTIVATED_ACCESS_ERROR_MESSAGE
} from 'utilities/constants';
import { showSuccessToast, showErrorToast } from 'utilities/notificationUtils';
import { pageUrl } from 'utilities/pageUrl';
import { decodeRedirectTarget } from 'utilities/urlUtils';

import { LoginForm } from './LoginForm/LoginForm';

export const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [targetRedirect, setTargetRedirect] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const toastType = params.get('toast_type');
    const toastMessage = params.get('toast_message');
    const deactivatedAccess = params.get(DEACTIVATED_ACCESS_QUERY_PARAM);

    if (toastType && toastMessage) {
      const decodedMsg = decodeURIComponent(toastMessage);
      if (toastType === 'success') showSuccessToast(decodedMsg);
      else showErrorToast(decodedMsg);

      // Remove the toast params but preserve others
      params.delete('toast_type');
      params.delete('toast_message');
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }

    if (deactivatedAccess === 'true') {
      showErrorToast(DEACTIVATED_ACCESS_ERROR_MESSAGE);

      params.delete(DEACTIVATED_ACCESS_QUERY_PARAM);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [location, navigate]);

  // TODO: clean up this membership params stuff
  // the additional params from the target are being parsed as if they are their own params
  // they need to be considered one param, so we can redirect to a specific part of the booking flow
  // this is for the employer membership flow currently
  // side: also check ProtectedRoute where pathname is pageUrl.booking.book()
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = decodeRedirectTarget(params.get('target'));
    const membershipId = params.get('membership_id');
    const enterpriseMembership = params.get('enterprise_membership');

    if (target)
      setTargetRedirect(
        `${target}${membershipId ? `&membership_id=${membershipId}` : ''}${
          enterpriseMembership ? `&enterprise_membership=${enterpriseMembership}` : ''
        }`
      );

    checkForAutomaticLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const checkForAutomaticLogout = () => {
    const params = new URLSearchParams(location.search);
    const loggedOut = params.get('loggedOut');

    if (loggedOut && !document.hidden) {
      analytics.track(analytics.EVENTS.PATIENT_AUTOMATICALLY_LOGGED_OUT);
    }
  };

  const redirectIfLoggedIn = (isLoggedIn: boolean) => {
    if (!isLoggedIn) return;

    if (targetRedirect) {
      navigate(targetRedirect, { replace: true });
      return;
    }
    navigate(pageUrl.dashboard(), { replace: true });
  };

  const onGoogleAuthError = (googleAuthError: GoogleAuthError, googleUserId: string | null) => {
    if (!googleUserId) return;
    if (!googleAuthError) return;

    if (googleAuthError.type === 'UserAlreadyExistsAsEmail') {
      showErrorToast(
        'You already have an account using your email address and password. Please login'
      );
      return;
    }
    if (googleAuthError && googleAuthError.type !== 'UserHasNotSignedUp') return;
    navigate(getCreateAccountPath());
  };

  const getCreateAccountPath = () => {
    if (targetRedirect) {
      return pageUrl.createAccount({
        step: 'create_account',
        target: targetRedirect,
        previous: 'login'
      });
    }

    return pageUrl.createAccount({ step: 'create_account', previous: 'login' });
  };

  const goToCreateAccount = () => navigate(getCreateAccountPath());

  return (
    <>
      <Helmet>
        <title>Kindbody | Log In</title>
      </Helmet>

      <AuthFlowLayout
        title="Log In"
        renderMainContent={() => {
          return (
            <>
              <LoginForm
                onLogin={() => redirectIfLoggedIn(true)}
                onError={(error, email, password) => {
                  if (error.type === 'Unconfirmed') {
                    dispatch(loginErrorHandled());
                    navigate(pageUrl.emailAddressVerification(), { state: { email, password } });
                  }
                }}
                onGoogleAuthError={onGoogleAuthError}
                onForgotPassword={() => {
                  navigate(pageUrl.passwordReset());
                }}
                onSignUpPress={goToCreateAccount}
              />

              <ActivateBenefitContainer>
                <ActivateBenefit>
                  <div>
                    <Text tag="p" size="md" fontStyle="medium">
                      Does your employer have the Kindbody benefit?
                    </Text>
                  </div>
                  <div>
                    <Button
                      label="ACTIVATE YOUR BENEFIT"
                      category="primary-dark"
                      fullWidth
                      aria-label="Activate your employer Kindbody benefit"
                      onClick={() =>
                        navigate(
                          pageUrl.createAccount({
                            step: 'create_account',
                            target: pageUrl.enterpriseMembershipAccessCode()
                          })
                        )
                      }
                    />
                  </div>
                </ActivateBenefit>
              </ActivateBenefitContainer>
            </>
          );
        }}
      />
    </>
  );
};
